<template>
  <div class="flex flex-col w-full justify-center relative">
    <form
      class="w-full space-y-6"
      @keydown.right.prevent="next"
      @submit.prevent="next"
      @keydown.enter.prevent="next"
      @keydown.esc="$emit('close')"
    >
      <div
        @click="showTooltip = !showTooltip"
        class="flex flex-row space-x-2 text-bright-gray dark:text-white text-2xl lg:text-4xl font-bold"
      >
        <h2>{{ item.questionDisplay }}</h2>
        <span v-if="item.required" class="text-ruby">*</span>
        <span class="cursor-pointer" v-if="item.tooltip.length"
          ><InformationCircleIcon class="w-8 h-8 text-gold"
        /></span>
      </div>
      <label for="" class="text-navada text-sm">{{ item.placeholder }} </label>
      <input
        v-focus
        :placeholder="placeholder"
        v-model="selected"
        @input="checkInput"
        class="flex rounded-lg w-full h-[60px] text-xl border-gold dark:text-white dark:bg-[#1b1c20] dark:border-gold focus:ring-0 border-2 focus focus:border-gold"
        type="text"
      />
      <!-- Desktop action -->
      <div
        class="hidden md:flex md:w-full flex-col md:flex-row justify-between w-full pt-4"
      >
        <div class="flex flex-row space-x-4">
          <button
            type="button"
            @click="next"
            class="inline-flex items-center justify-center text-center min-w-0 flex-shrink-0 font-bold text-sm px-8 py-2 bg-gold-alt text-black rounded-full focus:outline-none"
          >
            <span v-if="!isLoading">{{ t("input.continue") }}</span>
            <span v-else class="space-x-4 flex flex-row"
              ><span>{{ t("input.saving") }}</span>
              <Spinner text-color="text-black"
            /></span>
          </button>
          <button
            type="submit"
            class="inline-flex items-center justify-center space-x-1 font-bold text-sm px-8 py-2 text-white rounded-full focus:outline-none"
          >
            <span class="text-navada">press</span>
            <span class="text-black dark:text-white">{{
              t("input.enter")
            }}</span>
            <span>
              <ReturnIcon />
            </span>
          </button>
        </div>
          <div class="flex item-center space-x-6">
              <button
                  v-if="!item.required"
                  @click="$emit('skip', item, selected)"
                  class="inline-flex items-center justify-center py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
              >
            <span class="text-black dark:text-white">{{
                    t("input.skip")
                }}</span>
                  <SkipIcon />
              </button>
              <button
                  @click="$emit('close')"
                  class="inline-flex items-center justify-center space-x-1 font-bold text-sm py-2 text-white rounded-full focus:outline-none"
              >
                  <span class="text-black dark:text-white"></span>
                  <BackIcon />
              </button>
          </div>
      </div>
      <!-- Mobile action -->
      <div class="flex w-full flex-col pt-4 md:hidden space-y-4">
        <div class="flex flex-row w-full">
          <button
            type="button"
            @click="next"
            class="inline-flex w-full items-center justify-center text-center min-w-0 flex-shrink-0 font-bold text-sm px-8 py-2 bg-gold-alt text-black rounded-full focus:outline-none"
          >
            <span v-if="!isLoading">{{ t("input.continue") }}</span>
            <span v-else class="space-x-4 flex flex-row"
              ><span>{{ t("input.saving") }}</span>
              <Spinner text-color="text-black"
            /></span>
          </button>
        </div>
        <div class="flex flex-row justify-end">
            <div class="flex item-center space-x-6">
                <button
                    v-if="!item.required"
                    @click="$emit('skip', item, selected)"
                    class="inline-flex items-center justify-center py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
                >
            <span class="text-black dark:text-white">{{
                    t("input.skip")
                }}</span>
                    <SkipIcon />
                </button>
                <button
                    @click="$emit('close')"
                    class="inline-flex items-center justify-center space-x-1 font-bold text-sm py-2 text-white rounded-full focus:outline-none"
                >
                    <span class="text-black dark:text-white"></span>
                    <BackIcon />
                </button>
            </div>
        </div>
      </div>
    </form>
    <!-- Tooltip -->
    <tooltip-modal
      :show="showTooltip && item.tooltip.length"
      :tooltip="item.tooltip"
      @close="showTooltip = !showTooltip"
    />
    <!-- End tooltip -->

    <!-- Error -->
    <error-modal
      :show="showError"
      :error="error"
      @close="
        showError = !showError;
        error = '';
      "
    />
    <!-- End Error -->
  </div>
</template>

<script>
import { InformationCircleIcon } from "@heroicons/vue/solid";
import { computed, reactive, toRefs } from "vue";
import Spinner from "../Spinner";
import { useI18n } from "vue-i18n";
import ReturnIcon from "../icons/ReturnIcon";
import SkipIcon from "../icons/SkipIcon";
import BackIcon from "../icons/BackIcon";

export default {
  name: "Text",
  components: {
    SkipIcon,
    ReturnIcon,
    Spinner,
    InformationCircleIcon,
    BackIcon,
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const answer = computed(() => props.item.answer);

    const isPhone = computed(() => props.item.key === "business_telephone");

    const placeholder = computed(() => {
      if (props.item.question?.placeholder) {
        return props.item.question.placeholder[t("locale")];
      }
    });

    const state = reactive({
      showTooltip: false,
      showError: false,
      error: "",
      selected: answer.value ? answer.value.answer : "",
    });

    const next = () => {
      if (
        props.item.required &&
        (state.selected === null ||
          state.selected === "" ||
          state.selected.length === 0)
      ) {
        state.showError = true;
        state.error = t("input.required");
        return;
      }
      emit("next", props.item, state.selected);
    };

    const checkInput = () => {
      /*if (isPhone.value) {
                      const x = state.selected.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
                      state.selected = !x[2] ? x[1] : x[1] + '-' + x[2] + (x[3] ? '-' + x[3] : '');
                  }*/
    };

    return {
      t,
      checkInput,
      placeholder,
      next,
      ...toRefs(state),
    };
  },
};
</script>
